/* eslint-disable @nx/enforce-module-boundaries */
import { ChevronDownNew, CheckGreenIcon } from '@mybridge/icons';
import { useDirection } from '@mybridge/ui/custom-hooks/useDirection';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { useDisclosure } from '@mybridge/ui/hooks';
import { Input, InputGroup, InputRightElement } from '@mybridge/ui/input';
import { List, ListItem } from '@mybridge/ui/list';
import {
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
} from '@mybridge/ui/popover';
import { Spinner } from '@mybridge/ui/spinner';
import { Text, HStack } from '@mybridge/ui';
import { useRef, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Flex } from '@chakra-ui/react';

export const OnboardingCustomDropdown = ({
  value,
  onChange,
  clearOnSelect = false,
  allowCreate = true,
  children,
  keyName = 'name',
  onSearch,
  isLoading,
  createLoading,
  field,
  data = [],
  inputGroupProps,
  inputRElementProp,
  inputProps,
  onEscape,
  placeHolder,
  isTranslationDropdown,
  onCreateItem,
  ...props
}) => {
  const inputRef = useRef();
  const containerRef = useRef();
  const [search, setSearch] = useState(value || '');
  const disc = useDisclosure();
  const { t } = useTranslator();
  const { userProfileInfo } = useSelector((state) => state.userProfile);
  const direction = useDirection();

  // Update search when value prop changes
  useEffect(() => {
    if (value !== undefined) {
      setSearch(value || ''); // Ensure it's always a string
    }
  }, [value]);

  const onSelect = (item) => {
    if (item) {
      const selectedValue = clearOnSelect ? '' : item?.[keyName];
      setSearch(selectedValue || ''); // Ensure it's always a string
      onSearch?.(selectedValue || '');
      onChange(field ? item?.[field] : item?.name);
      disc?.onClose?.();
    }
  };

  const handleSelect = (item) => () => onSelect(item);

  const onSearchChange = (val) => {
    const stringVal = val || ''; // Ensure it's always a string
    setSearch(stringVal);
    onSearch?.(stringVal);
  };

  const onKeyDown = (e) => {
    if (e.keyCode !== 13 && e.keyCode !== 27) return;
    e.preventDefault?.();
    e.stopPropagation?.();
  };

  const onKeyUp = (e) => {
    e.preventDefault?.();
    e.stopPropagation?.();
    if (e.keyCode === 27) {
      inputRef.current.value = '';
      onEscape?.();
      return;
    }
    if (e.keyCode === 13) {
      onChange?.(field ? e?.target?.value : { [keyName]: e?.target?.value });
      inputRef.current.value = '';
      return;
    }
    disc?.onOpen?.();
  };

  // Commenting out the handleCreateItem function as we're removing create functionality
  /*
  const handleCreateItem = () => {
    if (onCreateItem && search) {
      onCreateItem(search);
      disc?.onClose?.();
    } else {
      onSelect({ [keyName]: search });
    }
  };
  */

  const languages = [
    { name: 'Arabic - العربية', lang_code: 'ar' },
    { name: 'Chinese - 中文', lang_code: 'zh-cn' },
    { name: 'English', lang_code: 'en' },
    { name: 'French - Français', lang_code: 'fr' },
    { name: 'German - Deutsch', lang_code: 'de' },
    { name: 'Hindi - हिंदी', lang_code: 'hi' },
    { name: 'Indonesian - Bahasa Indonesia', lang_code: 'id' },
    { name: 'Italian - Italiano', lang_code: 'it' },
    { name: 'Japanese - 日本語', lang_code: 'ja' },
    { name: 'Korean - 한국어', lang_code: 'ko' },
    { name: 'Portuguese - Português', lang_code: 'pt' },
    { name: 'Russian - Русский', lang_code: 'ru' },
    { name: 'Spanish - Español', lang_code: 'es' },
    { name: 'Turkish - Türkçe', lang_code: 'tr' },
    { name: 'Vietnamese - Tiếng Việt', lang_code: 'vi' },
  ];

  const getSelectedLanguageName = () => {
    if (!isTranslationDropdown) return search;
    const selectedLangCode = typeof value === 'string' ? value : value?.lang_code;
    return (
      languages.find((lang) => lang.lang_code === selectedLangCode)?.name || ''
    );
  };

  // Ensure search is always a string before using string methods
  const searchString = String(search || '');

  const filteredLanguages =
    searchString.trim().length <= 1
      ? languages
      : languages.filter((lang) =>
          lang.name.toLowerCase().includes(searchString.toLowerCase())
        );

  return (
    <Popover
      {...disc}
      ref={containerRef}
      matchWidth={true}
      initialFocusRef={inputRef}
      placement="bottom-start"
    >
      <PopoverTrigger>
        <InputGroup {...inputGroupProps}>
          <InputRightElement
            {...(direction === 'rtl' ? { left: '0rem', right: 'auto' } : {})}
            onClick={() => {
              onSearchChange(inputRef?.current?.value ?? '');
              disc?.onOpen?.();
            }}
            h="40px"
            cursor="pointer"
          >
            <ChevronDownNew width="24" height="24" />
          </InputRightElement>

          {children ? (
            children?.({
              inputRef,
              ref: inputRef,
              value: searchString,
              disc,
              onChange: onSearchChange,
              onKeyUp,
              onKeyDown,
            })
          ) : (
            <Input
              placeholder={placeHolder || t('jobs.select') || 'select'}
              sx={{ textTransform: 'capitalize' }}
              ref={inputRef}
              value={
                isTranslationDropdown
                  ? searchString || getSelectedLanguageName()
                  : searchString
              }
              onKeyUp={onKeyUp}
              onKeyDown={onKeyDown}
              onClick={() => disc?.onOpen?.()}
              onChange={(e) => {
                onSearchChange(e.target.value);
                if (!disc.isOpen) {
                  disc?.onOpen?.();
                }
              }}
              {...props}
              {...inputProps}
            />
          )}
        </InputGroup>
      </PopoverTrigger>

      {disc?.isOpen && (searchString.length > 0 || data?.length > 0) && (
        <PopoverContent p={0} w="100%" overflowY="auto">
          {isTranslationDropdown ? (
            <PopoverBody p={0}>
              <List>
                {filteredLanguages.map((lang, index) => (
                  <ListItem
                    key={index}
                    _hover={{ bg: 'gray.100' }}
                    px={4}
                    py={2}
                    bg={
                      lang?.lang_code === userProfileInfo?.lang
                        ? 'gray.200'
                        : 'white'
                    }
                    onClick={handleSelect(lang)}
                  >
                    <Flex justify="space-between" align="center" w="100%">
                      <span>{lang.name}</span>
                      {lang?.lang_code === userProfileInfo?.lang && (
                        <CheckGreenIcon />
                      )}
                    </Flex>
                  </ListItem>
                ))}
                {filteredLanguages.length === 0 && (
                  <ListItem px={4} py={2} color="gray.500" textAlign="center">
                    {t('searchPage.noResultsFound') || 'No results found'}
                  </ListItem>
                )}
              </List>
            </PopoverBody>
          ) : (
            <PopoverBody p={0}>
              <List maxH="300px" overflowY="auto">
                {/* Commenting out the create item functionality
                {allowCreate && search?.length > 0 && (
                  <ListItem
                    _hover={{ bg: 'gray.100' }}
                    px={4}
                    py={2}
                    cursor="pointer"
                    onClick={handleCreateItem}
                  >
                    <HStack>
                      {createLoading && <Spinner />}
                      <Text>{t('myDisk.create') || 'Create'} "{search}"</Text>
                    </HStack>
                  </ListItem>
                )}
                */}
                {!isLoading &&
                  (searchString?.length <= 1 ? data : data.filter((c) =>
                    c?.[keyName]
                      ?.toLowerCase?.()
                      ?.includes(searchString?.toLowerCase?.())
                  ))?.map?.((c, i) => (
                    <ListItem
                      key={i}
                      _hover={{ bg: 'gray.100' }}
                      px={4}
                      py={2}
                      cursor="pointer"
                      onClick={handleSelect(c)}
                    >
                      {c?.[keyName]}
                    </ListItem>
                  ))}

                {!isLoading &&
                  searchString?.length > 1 &&
                  data.filter?.((c) =>
                    c?.[keyName]?.toLowerCase?.()?.includes(searchString?.toLowerCase?.())
                  )?.length === 0 && (
                  // !allowCreate && (
                    <ListItem
                      px={4}
                      py={2}
                      color="gray.500"
                      textAlign="center"
                    >
                      {t('searchPage.noResultsFound') || 'No results found'}
                    </ListItem>
                  )}
              </List>
            </PopoverBody>
          )}
        </PopoverContent>
      )}
    </Popover>
  );
};
